import React from 'react'
import history from 'browserHistory'

// Controls inside a row that do their own thing on click and must not
// open the row's page.
const INTERACTIVE = 'button, input, select, textarea, a, [role="button"], [role="checkbox"]'

const isOwnClick = (event) => (
  event.currentTarget.contains(event.target) &&
  event.target.closest(INTERACTIVE) === event.currentTarget
)

const isModified = (event) => (
  event.metaKey || event.altKey || event.ctrlKey || event.shiftKey
)

/**
 * A list row rendered as a real link, so it can be opened in a new tab or
 * window (middle click, Ctrl/Cmd/Shift + click, "Open link in new tab").
 * A plain click keeps the in-app navigation.
 *
 * `to` is an in-app path, `href` an external URL that always opens in a new
 * tab. `onOpen` runs whenever the row is opened, in any of these ways.
 */
const RowLink = ({ to, href, onOpen, className, style, children, ...rest }) => {
  const stopRowNavigation = (event) => {
    // Clicks on a button, checkbox, chip... inside the row would also make
    // the browser follow the row link. Their handlers usually stop
    // propagation, so this has to happen in the capture phase. Checkbox
    // inputs are left alone, as preventing their click would undo the toggle
    // (and the browser does not follow the link for them anyway).
    if (
      event.currentTarget.contains(event.target) &&
      !isOwnClick(event) &&
      event.target.tagName !== 'INPUT'
    ) {
      event.preventDefault()
    }
  }

  const handleClick = (event) => {
    if (!isOwnClick(event)) {
      return
    }
    if (onOpen) {
      onOpen(event)
    }
    if (href || event.button !== 0 || isModified(event)) {
      // The browser opens it, in a new tab or window when asked to
      return
    }
    event.preventDefault()
    history.push(to)
  }

  const handleAuxClick = (event) => {
    if (event.button === 1 && isOwnClick(event) && onOpen) {
      onOpen(event)
    }
  }

  const externalProps = href ? { target: '_blank', rel: 'noopener noreferrer' } : {}

  return (
    <a
      href={href || history.createHref({ pathname: to })}
      className={className}
      style={{ color: 'inherit', textDecoration: 'none', ...style }}
      onClickCapture={stopRowNavigation}
      onAuxClickCapture={stopRowNavigation}
      onClick={handleClick}
      onAuxClick={handleAuxClick}
      {...externalProps}
      {...rest}
    >
      {children}
    </a>
  )
}

export default RowLink
